import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
    useMediaQuery,
    useTheme,
    Drawer,
    List,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    IconButton,
    AppBar,
    Toolbar,
    Badge,
    ListItemButton
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useReservationAdminAPI} from "../service/useReservationAdminAPI";
import {useAuth0} from "@auth0/auth0-react";
import AppSnackbar from '../common/AppSnackbar';
import PetsIcon from "@mui/icons-material/Pets";
import {useTenantConfig} from "../security/tenant-config-context";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

const BeheerSideNav = ({drawerOpen, setDrawerOpen, collapsed, setCollapsed}) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [newReservationCount, setNewReservationCount] = useState(0);
    const {getCountNewReservations} = useReservationAdminAPI();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {loginWithRedirect, getAccessTokenSilently, isAuthenticated, user} = useAuth0();
    const isAdmin = user && user['https://tailvation.com/app_metadata']?.roles?.includes('cat-admin');
    const [showNavigation, setShowNavigation] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const {cfg} = useTenantConfig();
    const website = cfg.ui?.website;
    const logoUrl = cfg.ui?.logoUrl;
    const name = cfg.name;
    const isHotelFlow = cfg?.businessTypes?.includes('HOTEL_BASED');

    useEffect(() => {
        if (!isMobile) setDrawerOpen(true);

        const checkTokenExpiry = async () => {
            try {
                await getAccessTokenSilently();
            } catch {
                await loginWithRedirect();
            }
        };

        if (isAuthenticated) {
            checkTokenExpiry().then();
            const interval = setInterval(checkTokenExpiry, 60000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

    useEffect(() => {
        if (isAdmin) {
            getCountNewReservations(isHotelFlow)
                .then(setNewReservationCount)
                .catch(() => {
                    setSnackbarMessage(t('newReservationsError'));
                    setOpenSnackbar(true);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    useEffect(() => {
        if (isAuthenticated) {
            setShowNavigation(true);
        }
    }, [isAuthenticated]);

    const handleDrawerToggle = () => setDrawerOpen(prev => !prev);

    const handleNavigation = (to) => {
        if (isMobile) setDrawerOpen(false);
        navigate(to);
    };

    const drawerContent = (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box sx={{flexGrow: 1, p: 2, alignItems: 'center', display: 'flex', flexDirection: 'column'}}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '25%',
                        right: -6,
                        transform: 'translateY(-50%)',
                        bgcolor: 'background.paper',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        boxShadow: 1,
                        borderRadius: '25%',
                        zIndex: 1301
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={() => setCollapsed(prev => !prev)}
                        sx={{
                            width: 32,
                            height: 32,
                            p: 0,
                        }}
                    >
                        {collapsed ? <ChevronRightIcon fontSize="small"/> : <ChevronLeftIcon fontSize="small"/>}
                    </IconButton>
                </Box>
                {(() => {
                    const isDefaultLogo = typeof logoUrl === 'string' && logoUrl.includes('https://tailvation.be/logo');
                    if (!isDefaultLogo && logoUrl) {
                        return (
                            <a href={website}>
                                <img src={logoUrl} alt='Logo' style={{maxWidth: '100%', maxHeight: '60px'}}/>
                            </a>
                        );
                    }
                    return (
                        <Box sx={{width: '100%', maxWidth: 220}}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 60,
                                    borderRadius: 2,
                                    bgcolor: 'grey.100',
                                    border: '2px dashed',
                                    borderColor: 'grey.400',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    mb: 0.5
                                }}
                            >
                                <ImageOutlinedIcon sx={{color: 'grey.500'}}/>
                                {!collapsed && (
                                    <Typography variant="caption" sx={{display: 'block', textAlign: 'center', color: 'text.secondary'}}>
                                        {t('yourLogoHere')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })()}
                {!drawerOpen || collapsed ? null : <Typography color='secondary'>{name}</Typography>}
                {showNavigation && (
                    <List>
                        {[
                            {
                                to: "/beheer/dashboard",
                                icon: <DashboardIcon color='primary'/>,
                                label: 'dashboard'
                            },
                            {
                                to: "/beheer/dieren",
                                icon: <PetsIcon color='primary'/>,
                                label: 'animals'
                            },
                            {
                                to: "/beheer/klanten",
                                icon: <PersonIcon color='primary'/>,
                                label: 'customers'
                            },
                            ...(!isXs && isHotelFlow ? [{
                                to: "/beheer/reservatie-overzicht",
                                icon: <NewReleasesIcon color='primary'/>,
                                label: 'newReservations',
                                badge: newReservationCount
                            }] : []),
                            ...(!isXs && !isHotelFlow ? [{
                                to: "/beheer/afspraak-overzicht",
                                icon: <NewReleasesIcon color='primary'/>,
                                label: 'newAppointments',
                                badge: newReservationCount
                            }] : []),
                            ...(!isXs && isHotelFlow ? [{
                                to: "/beheer/reservatie-beheer",
                                icon: <ViewModuleIcon color='primary'/>,
                                label: 'reservationOverview'
                            }] : []),
                            ...(!isXs && !isHotelFlow ? [{
                                to: "/beheer/afspraak-beheer",
                                icon: <ViewModuleIcon color='primary'/>,
                                label: 'appointmentOverview'
                            }] : []),
                            {
                                to: "/beheer/vacation",
                                icon: <BeachAccessIcon color='primary'/>,
                                label: 'vacations'
                            },
                            ...(isHotelFlow ? [{
                                to: "/beheer/reservatie-toevoegen",
                                icon: <EditCalendarIcon color='primary'/>,
                                label: 'addReservation'
                            }] : []),
                            ...(!isHotelFlow ? [{
                                to: "/beheer/afspraak-toevoegen",
                                icon: <EditCalendarIcon color='primary'/>,
                                label: 'addAppointment'
                            }] : []),
                            ...(!isXs ? [{
                                to: "/beheer/configuratie",
                                icon: <ScheduleIcon color='primary'/>,
                                label: 'configurationLabel'
                            }] : [])].map(({to, icon, label, badge}) => (
                            <ListItemButton key={label} onClick={() => handleNavigation(to)} sx={{justifyContent: drawerOpen ? 'flex-start' : 'center'}} data-testid={`nav-item-${label}`}>
                                <ListItemIcon sx={{
                                    minWidth: 0,
                                    mr: drawerOpen ? 2 : 0,
                                    justifyContent: 'center',
                                    color: 'inherit'
                                }} data-testid={`nav-icon-${label}`}>{icon}</ListItemIcon>
                                {!collapsed && <ListItemText primary={t(label)} data-testid={`nav-text-${label}`}/>}
                                {!collapsed && badge && badge > 0 && drawerOpen && (
                                    <Badge badgeContent={badge} color='primary' sx={{ml: 2}} data-testid={`nav-badge-${label}`}/>
                                )}
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );

    return (
        <div>
            {isMobile && <AppBar position='fixed' sx={{background: 'transparent', boxShadow: 'none'}}>
                <Toolbar>
                    <IconButton edge='start' onClick={handleDrawerToggle} sx={{mr: 2}}>
                        <MenuIcon/>
                    </IconButton>
                </Toolbar>
            </AppBar>}

            <Drawer
                variant={isMobile ? "temporary" : "persistent"}
                open={isMobile ? drawerOpen : true}
                onClose={handleDrawerToggle}
                ModalProps={{keepMounted: true}}
                sx={{
                    width: collapsed ? 60 : 240,
                    '& .MuiDrawer-paper': {
                        width: collapsed ? 60 : 240,
                        transition: 'width 0.3s',
                        overflowX: 'hidden'
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            {isMobile && <Toolbar/>}
            <AppSnackbar openSnackbar={openSnackbar} snackbarMessage={snackbarMessage} setOpenSnackbar={setOpenSnackbar}/>
        </div>
    );
};

export default BeheerSideNav;
